import React, { useState } from 'react';
import { Lightbulb, TrendingUp, AlertCircle, ChevronRight, ThumbsUp, ThumbsDown } from 'lucide-react';
import { useApp } from '../contexts/AppContext';

const AIInsights = () => {
    const { insights } = useApp();
    const [filter, setFilter] = useState('all');

    const filteredInsights = filter === 'all'
        ? insights
        : insights.filter(i => i.priority === filter);

    const getPriorityColor = (priority) => {
        return priority === 'high' ? 'error' : priority === 'medium' ? 'warning' : 'info';
    };

    const getTypeIcon = (type) => {
        const icons = {
            'creative-performance': '🎨',
            'timing-optimization': '⏰',
            'budget-optimization': '💰',
            'audience-insight': '👥',
            'platform-recommendation': '📱',
        };
        return icons[type] || '💡';
    };

    return (
        <div className="animate-fadeIn">
            <div style={{ marginBottom: 'var(--space-8)' }}>
                <h1>AI Insights & Recommendations</h1>
                <p className="text-secondary">Data-driven recommendations to optimize your campaigns.</p>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-6" style={{ marginBottom: 'var(--space-8)' }}>
                <div className="card-gradient">
                    <div style={{ fontSize: 'var(--font-size-3xl)', marginBottom: 'var(--space-2)' }}>
                        {insights.filter(i => i.priority === 'high').length}
                    </div>
                    <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600 }}>
                        High Priority Insights
                    </div>
                    <p style={{ margin: 0, opacity: 0.9 }}>Requires immediate attention</p>
                </div>

                <div className="card">
                    <div style={{ fontSize: 'var(--font-size-3xl)', marginBottom: 'var(--space-2)', color: 'var(--color-success)' }}>
                        +$6.1K
                    </div>
                    <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600 }}>
                        Projected Impact
                    </div>
                    <p style={{ margin: 0 }}>If all recommendations applied</p>
                </div>

                <div className="card">
                    <div style={{ fontSize: 'var(--font-size-3xl)', marginBottom: 'var(--space-2)', color: 'var(--color-primary)' }}>
                        89%
                    </div>
                    <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600 }}>
                        Avg Confidence
                    </div>
                    <p style={{ margin: 0 }}>Based on historical data</p>
                </div>
            </div>

            {/* Filter */}
            <div style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-6)' }}>
                <button
                    className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setFilter('all')}
                >
                    All
                </button>
                <button
                    className={`btn ${filter === 'high' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setFilter('high')}
                >
                    High Priority
                </button>
                <button
                    className={`btn ${filter === 'medium' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setFilter('medium')}
                >
                    Medium
                </button>
                <button
                    className={`btn ${filter === 'low' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setFilter('low')}
                >
                    Low
                </button>
            </div>

            {/* Insights List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                {filteredInsights.map((insight) => (
                    <div key={insight.id} className="card" style={{ border: `2px solid var(--color-${getPriorityColor(insight.priority)})` }}>
                        <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
                            {/* Icon */}
                            <div
                                style={{
                                    width: '60px',
                                    height: '60px',
                                    background: `var(--color-${getPriorityColor(insight.priority)})`,
                                    borderRadius: 'var(--radius-lg)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 'var(--font-size-2xl)',
                                    flexShrink: 0,
                                }}
                            >
                                {getTypeIcon(insight.type)}
                            </div>

                            {/* Content */}
                            <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-1)' }}>
                                            <h3 style={{ margin: 0 }}>{insight.title}</h3>
                                            <span className={`badge badge-${getPriorityColor(insight.priority)}`}>
                                                {insight.priority} priority
                                            </span>
                                        </div>
                                        <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
                                            {insight.description}
                                        </p>
                                    </div>

                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-1)' }}>
                                            Confidence
                                        </div>
                                        <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--color-success)' }}>
                                            {insight.confidence}%
                                        </div>
                                    </div>
                                </div>

                                {/* Impact */}
                                <div
                                    style={{
                                        padding: 'var(--space-3)',
                                        background: 'var(--color-bg-secondary)',
                                        borderRadius: 'var(--radius-md)',
                                        marginBottom: 'var(--space-4)',
                                    }}
                                >
                                    <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-success)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                        <TrendingUp size={16} />
                                        Projected Impact: {insight.impact}
                                    </div>
                                </div>

                                {/* Action Items */}
                                {insight.actionItems && insight.actionItems.length > 0 && (
                                    <div style={{ marginBottom: 'var(--space-4)' }}>
                                        <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--space-2)' }}>
                                            Recommended Actions:
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                            {insight.actionItems.map((action, index) => (
                                                <div
                                                    key={index}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 'var(--space-2)',
                                                        fontSize: 'var(--font-size-sm)',
                                                    }}
                                                >
                                                    <ChevronRight size={16} style={{ color: 'var(--color-primary)' }} />
                                                    {action}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Actions */}
                                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                                    <button className="btn btn-primary">
                                        Apply Recommendations
                                    </button>
                                    <button className="btn btn-ghost">
                                        <ThumbsUp size={16} />
                                        Helpful
                                    </button>
                                    <button className="btn btn-ghost">
                                        <ThumbsDown size={16} />
                                        Not Helpful
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {filteredInsights.length === 0 && (
                <div className="empty-state">
                    <Lightbulb size={64} className="empty-state-icon" />
                    <h3 className="empty-state-title">No insights found</h3>
                    <p className="empty-state-description">
                        Try adjusting your filters or check back later for new recommendations
                    </p>
                </div>
            )}
        </div>
    );
};

export default AIInsights;
